import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Camera, X, Loader2 } from 'lucide-react';
import { auth, storage } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { updateProfile } from 'firebase/auth';
import { toast } from 'react-hot-toast';
import ProfilePhotoCropper from './ProfilePhotoCropper';

interface UserPhotoUploaderProps {
  currentPhotoURL: string | null;
  displayName?: string | null;
  onPhotoUpdate: (url: string | null) => void;
}

export default function UserPhotoUploader({
  currentPhotoURL,
  displayName,
  onPhotoUpdate,
}: UserPhotoUploaderProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [showCropper, setShowCropper] = useState(false);

  const handleUpload = async (file: File) => {
    setSelectedImage(file);
    setShowCropper(true);
  };

  const handleCroppedImage = async (blob: Blob) => {
    if (!auth.currentUser) {
      toast.error('Vous devez être connecté pour modifier la photo');
      return;
    }

    setIsUploading(true);
    setShowCropper(false);

    try {
      // Si une photo existe déjà, essayer de la supprimer d'abord
      if (currentPhotoURL) {
        try {
          // Extraire le chemin du stockage à partir de l'URL
          const urlPath = currentPhotoURL.split('?')[0];
          const storagePath = urlPath.split('.com/o/')[1];
          if (storagePath) {
            const decodedPath = decodeURIComponent(storagePath);
            const oldRef = ref(storage, decodedPath);
            await deleteObject(oldRef);
            console.log('Ancienne photo supprimée avec succès');
          }
        } catch (deleteError) {
          console.warn('Erreur lors de la suppression de l\'ancienne photo:', deleteError);
          // Continuer même si la suppression échoue
        }
      }

      // Créer un nom de fichier unique
      const timestamp = Date.now();
      const fileName = `profile-${auth.currentUser.uid}-${timestamp}.jpg`;
      const filePath = `profile-photos/${auth.currentUser.uid}/${fileName}`;
      const fileRef = ref(storage, filePath);

      // Ajouter des métadonnées CORS
      const metadata = {
        contentType: 'image/jpeg',
        customMetadata: {
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
          'Access-Control-Allow-Headers': 'Content-Type, Authorization'
        }
      };

      // Uploader l'image recadrée
      await uploadBytes(fileRef, blob, metadata);
      const downloadURL = await getDownloadURL(fileRef);

      // Mettre à jour le profil utilisateur
      await updateProfile(auth.currentUser, {
        photoURL: downloadURL
      });

      // Mettre à jour l'état local
      setPreviewUrl(downloadURL);
      onPhotoUpdate(downloadURL);
      toast.success('Photo de profil mise à jour avec succès');
    } catch (error) {
      console.error('Erreur lors de l\'upload:', error);
      toast.error('Erreur lors de la mise à jour de la photo');
    } finally {
      setIsUploading(false);
      setSelectedImage(null);
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Validation de la taille du fichier (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La taille du fichier dépasse 5 Mo');
      return;
    }

    // Validation du type de fichier
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Format non supporté. Utilisez JPG ou PNG');
      return;
    }

    handleUpload(file);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    maxFiles: 1,
    disabled: isUploading,
  });

  const handleRemovePhoto = async () => {
    if (!auth.currentUser) return;
    
    setIsUploading(true);
    
    try {
      // Si une photo existe, essayer de la supprimer
      if (currentPhotoURL) {
        try {
          // Extraire le chemin du stockage à partir de l'URL
          const urlPath = currentPhotoURL.split('?')[0];
          const storagePath = urlPath.split('.com/o/')[1];
          if (storagePath) {
            const decodedPath = decodeURIComponent(storagePath);
            const oldRef = ref(storage, decodedPath);
            await deleteObject(oldRef);
          }
        } catch (deleteError) {
          console.warn('Erreur lors de la suppression de l\'ancienne photo:', deleteError);
        }
      }
      
      // Mettre à jour le profil utilisateur avec une photo vide
      await updateProfile(auth.currentUser, {
        photoURL: null
      });
      
      setPreviewUrl(null);
      onPhotoUpdate(null);
      toast.success('Photo de profil supprimée');
    } catch (error) {
      console.error('Erreur lors de la suppression de la photo:', error);
      toast.error('Erreur lors de la suppression de la photo');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <div {...getRootProps()} className="relative w-24 h-24 rounded-full overflow-hidden cursor-pointer group">
          <input {...getInputProps()} />
          {(previewUrl || currentPhotoURL) ? (
            <img
              src={previewUrl || currentPhotoURL || ''}
              alt="Photo de profil"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
              <Camera className="w-8 h-8 text-gray-400" />
            </div>
          )}
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Camera className="w-6 h-6 text-white" />
          </div>
        </div>

        {(currentPhotoURL || previewUrl) && !isUploading && (
          <button
            onClick={handleRemovePhoto}
            className="absolute -top-1 -right-1 p-1 bg-red-100 text-red-600 rounded-full hover:bg-red-200"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        {isUploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-full">
            <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
          </div>
        )}
      </div>

      <p className="mt-2 text-sm text-gray-500">Cliquez pour modifier la photo</p>

      {showCropper && selectedImage && (
        <ProfilePhotoCropper
          selectedImage={selectedImage}
          onSave={handleCroppedImage}
          onClose={() => {
            setShowCropper(false);
            setSelectedImage(null);
          }}
        />
      )}
    </div>
  );
}